import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { getCustomFields } from '@/lib/api';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Calendar } from '@/components/ui/calendar';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { Button } from '@/components/ui/button';
import { CalendarIcon, HelpCircle } from 'lucide-react';
import { format } from 'date-fns';
import { cn } from '@/lib/utils';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';

/**
 * CustomFieldsRenderer - Renders custom fields for an entity type
 * @param {string} entityType - The entity type (Client, Worker, Service Order, etc.)
 * @param {object} values - Current values for custom fields (key: field_name, value: field value)
 * @param {function} onChange - Callback when a field value changes (field_name, value)
 * @param {boolean} readOnly - If true, renders as display only
 * @param {string} mode - 'form' for edit mode, 'display' for view mode
 */
export default function CustomFieldsRenderer({ 
  entityType, 
  values = {}, 
  onChange, 
  readOnly = false,
  mode = 'form'
}) {
  const [fields, setFields] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadFields();
  }, [loadFields]);

  const loadFields = useCallback(async () => {
    try {
      const res = await getCustomFields({ entity_type: entityType });
      // Filter active fields and sort by display_order
      const activeFields = (res.data || [])
        .filter(f => f.is_active !== false && !f.is_deleted)
        .sort((a, b) => (a.display_order || 0) - (b.display_order || 0));
      setFields(activeFields);
    } catch (error) {
      console.error('Failed to load custom fields:', error);
    } finally {
      setLoading(false);
    }
  }, [entityType]);

  const handleChange = (fieldName, value) => {
    if (onChange) {
      onChange(fieldName, value);
    }
  };

  const renderField = (field) => {
    const value = values[field.field_name];
    const fieldId = `custom-field-${field.field_name}`;

    // Display mode - just show the value
    if (mode === 'display' || readOnly) {
      return (
        <div key={field.id} className="space-y-1" data-testid={`custom-field-display-${field.field_name}`}>
          <p className="text-xs text-muted-foreground">{field.field_label}</p>
          <p className="font-medium">
            {renderDisplayValue(field, value)}
          </p>
        </div>
      );
    }

    // Form mode - render input based on field type
    return (
      <div key={field.id} className="space-y-2" data-testid={`custom-field-${field.field_name}`}>
        <div className="flex items-center gap-2">
          <Label htmlFor={fieldId} className="text-sm">
            {field.field_label}
            {field.is_required && <span className="text-red-500 ml-1">*</span>}
          </Label>
          {field.help_text && (
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <HelpCircle className="w-3.5 h-3.5 text-muted-foreground cursor-help" />
                </TooltipTrigger>
                <TooltipContent>
                  <p className="max-w-xs text-xs">{field.help_text}</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          )}
        </div>
        {renderInput(field, value, fieldId)}
      </div>
    );
  };

  const renderDisplayValue = (field, value) => {
    if (value === null || value === undefined || value === '') {
      return <span className="text-muted-foreground italic">Not set</span>;
    }

    switch (field.field_type) {
      case 'Boolean':
        return value ? 'Yes' : 'No';
      case 'Date':
        try {
          return format(new Date(value), 'PPP');
        } catch {
          return value;
        }
      case 'Multi Select':
        if (Array.isArray(value)) {
          return (
            <div className="flex flex-wrap gap-1">
              {value.map((v, i) => (
                <Badge key={i} variant="secondary" className="text-xs">{v}</Badge>
              ))}
            </div>
          );
        }
        return value;
      case 'URL':
        return (
          <a href={value} target="_blank" rel="noopener noreferrer" className="text-teal-600 hover:underline">
            {value}
          </a>
        );
      case 'Email':
        return (
          <a href={`mailto:${value}`} className="text-teal-600 hover:underline">
            {value}
          </a>
        );
      case 'Phone':
        return (
          <a href={`tel:${value}`} className="text-teal-600 hover:underline">
            {value}
          </a>
        );
      default:
        return value;
    }
  };

  const renderInput = (field, value, fieldId) => {
    switch (field.field_type) {
      case 'Text':
        return (
          <Input
            id={fieldId}
            value={value || ''}
            onChange={(e) => handleChange(field.field_name, e.target.value)}
            placeholder={field.help_text || `Enter ${field.field_label.toLowerCase()}`}
            required={field.is_required}
          />
        );

      case 'Long Text':
        return (
          <Textarea
            id={fieldId}
            value={value || ''}
            onChange={(e) => handleChange(field.field_name, e.target.value)}
            placeholder={field.help_text || `Enter ${field.field_label.toLowerCase()}`}
            required={field.is_required}
            rows={3}
          />
        );

      case 'Number':
        return (
          <Input
            id={fieldId}
            type="number"
            value={value || ''}
            onChange={(e) => handleChange(field.field_name, e.target.value ? parseFloat(e.target.value) : '')}
            placeholder={field.help_text || `Enter ${field.field_label.toLowerCase()}`}
            required={field.is_required}
          />
        );

      case 'Date':
        return (
          <Popover>
            <PopoverTrigger asChild>
              <Button
                variant="outline"
                className={cn(
                  'w-full justify-start text-left font-normal',
                  !value && 'text-muted-foreground'
                )}
              >
                <CalendarIcon className="mr-2 h-4 w-4" />
                {value ? format(new Date(value), 'PPP') : <span>Pick a date</span>}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0" align="start">
              <Calendar
                mode="single"
                selected={value ? new Date(value) : undefined}
                onSelect={(date) => handleChange(field.field_name, date ? date.toISOString().split('T')[0] : '')}
                initialFocus
              />
            </PopoverContent>
          </Popover>
        );

      case 'Boolean':
        return (
          <div className="flex items-center gap-2">
            <Switch
              id={fieldId}
              checked={value || false}
              onCheckedChange={(checked) => handleChange(field.field_name, checked)}
            />
            <Label htmlFor={fieldId} className="text-sm text-muted-foreground">
              {value ? 'Yes' : 'No'}
            </Label>
          </div>
        );

      case 'Select':
      case 'Single Select':
        return (
          <Select
            value={value || ''}
            onValueChange={(v) => handleChange(field.field_name, v)}
          >
            <SelectTrigger>
              <SelectValue placeholder={`Select ${field.field_label.toLowerCase()}`} />
            </SelectTrigger>
            <SelectContent>
              {(field.field_options || []).map((option) => (
                <SelectItem key={option} value={option}>
                  {option}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        );

      case 'Multi Select':
        const selectedValues = Array.isArray(value) ? value : [];
        return (
          <div className="space-y-2">
            <div className="flex flex-wrap gap-2">
              {selectedValues.map((v, i) => (
                <Badge key={i} variant="secondary" className="gap-1">
                  {v}
                  <button
                    type="button"
                    onClick={() => handleChange(field.field_name, selectedValues.filter((_, idx) => idx !== i))}
                    className="ml-1 hover:text-destructive"
                  >
                    ×
                  </button>
                </Badge>
              ))}
            </div>
            <Select
              value=""
              onValueChange={(v) => {
                if (!selectedValues.includes(v)) {
                  handleChange(field.field_name, [...selectedValues, v]);
                }
              }}
            >
              <SelectTrigger>
                <SelectValue placeholder={`Add ${field.field_label.toLowerCase()}`} />
              </SelectTrigger>
              <SelectContent>
                {(field.field_options || [])
                  .filter(opt => !selectedValues.includes(opt))
                  .map((option) => (
                    <SelectItem key={option} value={option}>
                      {option}
                    </SelectItem>
                  ))}
              </SelectContent>
            </Select>
          </div>
        );

      case 'Email':
        return (
          <Input
            id={fieldId}
            type="email"
            value={value || ''}
            onChange={(e) => handleChange(field.field_name, e.target.value)}
            placeholder={field.help_text || 'email@example.com'}
            required={field.is_required}
          />
        );

      case 'Phone':
        return (
          <Input
            id={fieldId}
            type="tel"
            value={value || ''}
            onChange={(e) => handleChange(field.field_name, e.target.value)}
            placeholder={field.help_text || '+971 50 123 4567'}
            required={field.is_required}
          />
        );

      case 'URL':
        return (
          <Input
            id={fieldId}
            type="url"
            value={value || ''}
            onChange={(e) => handleChange(field.field_name, e.target.value)}
            placeholder={field.help_text || 'https://example.com'}
            required={field.is_required}
          />
        );

      default:
        return (
          <Input
            id={fieldId}
            value={value || ''}
            onChange={(e) => handleChange(field.field_name, e.target.value)}
            placeholder={field.help_text || `Enter ${field.field_label.toLowerCase()}`}
            required={field.is_required}
          />
        );
    }
  };

  if (loading) {
    return (
      <div className="space-y-3">
        {[1, 2].map((i) => (
          <div key={i} className="space-y-2">
            <div className="skeleton h-4 w-24 rounded" />
            <div className="skeleton h-10 w-full rounded" />
          </div>
        ))}
      </div>
    );
  }

  if (fields.length === 0) {
    return null;
  }

  // Memoize filtered fields based on mode
  const visibleFields = useMemo(() => 
    mode === 'display' 
      ? fields.filter(f => f.show_in_detail !== false)
      : fields,
    [fields, mode]
  );

  if (visibleFields.length === 0) {
    return null;
  }

  return (
    <div className={cn(
      mode === 'display' ? 'grid grid-cols-2 gap-4' : 'space-y-4'
    )}>
      {visibleFields.map(renderField)}
    </div>
  );
}
